// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2024-09-14 22:50:50 +0200 (Sat, 14 Sep 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 145747 $
//  Purpose   : layouts distribute parts in a html sekeleton
// =============================================================================
// ---Third-party imports
import React, {Component, useState} from 'react'
import PropTypes from 'prop-types'
import {Container, Tab, Loader, Button} from 'semantic-ui-react'
import {ReflexContainer, ReflexSplitter, ReflexElement} from 'react-reflex'
import 'react-reflex/styles.css'

// ---Framework imports
import connect from 'uix/connect'
import {each, has, get, size, dict, sorted, sum, mapItems, filterItems, flatten} from 'ldash'
import {ucFirst, canUseDOM} from 'uix/utils'
import log from 'uix/logging'
import {selectRequestClassCfg, getRunId, selectRequestI18N} from 'uix/selectors'
import {Actions} from 'uix/actions'
import {PortalTarget, AutoSizer, ResizableDialog} from 'uix/components'
import {useResizeObserver, useCallback} from 'uix/hooks'

import {createParts} from 'uix/parts'
import {translate} from 'uix/i18n'

// ---Local imports
import './layouts.scss'
import {persistSplitter} from './actions'

// ---Globals

class Layout extends Component {
  static propTypes = {
    parts: PropTypes.shape({
      classlevel: PropTypes.array.isRequired,
      tablevel: PropTypes.array.isRequired,
    }).isRequired,
  }

  name = () => 'Layout'
  cssClassName = () => {
    let {classname, tabname, theme = 'default'} = this.props
    let c = 'class' + ucFirst(classname || '')
    let t = 'tab' + ucFirst(tabname || '')

    return `layout ${this.name()} ${c} ${t} theme ${theme}`
  }

  render() {
    return <div className={this.cssClassName()}>LAYOUT</div>
  }
}

const orderSorted = parts => sorted(parts, ([part, cfg]) => parseInt(cfg.order || 0))
class Blank extends Layout {
  name = () => 'Blank'
  render() {
    let {classlevel, tablevel} = this.props.parts
    let parts = [...classlevel, ...tablevel]
    return <div className={this.cssClassName()}>{createParts(orderSorted(parts))}</div>
  }
}

class L1 extends Blank {
  /* future frontend layout?
   */
  name = () => 'L1'
}

class L1Col extends Blank {
  /* future frontend layout?
   */
  name = () => 'L1Col'
}

class L111 extends Layout {
  name = () => 'L111'
  alignParts() {
    /* create parts and group by align attribute
     */
    let parts = {}
    each(['top', 'left', 'center', 'right', 'bottom', 'dialog'], align => {
      let {classlevel, tablevel} = this.props.parts
      let filtered = [...classlevel, ...tablevel].filter(
        ([part, cfg]) => cfg.align == align || (!cfg.align && align == 'center')
      )
      parts[align] = createParts(orderSorted(filtered))
    })
    return parts
  }

  containerElement(parts, cprops) {
    return <div {...cprops}>{parts}</div>
  }

  container(parts, cprops) {
    if (!size(parts)) return null
    return this.containerElement(parts, cprops)
  }

  dialogContainer(parts) {
    if (!size(parts)) return null
    return (
      <ResizableDialog
        overflow="hidden"
        closeButton={false}
        name="LayoutDialogParts"
        storeKey="Global/LayoutDialogParts"
      >
        {parts}
      </ResizableDialog>
    )
  }

  render() {
    let {top, left, center, right, bottom, dialog} = this.alignParts()
    let centerParts = flatten([left, center, right].map(e => e || []))
    return (
      <div id="flexWrapper" className={this.cssClassName()}>
        {this.container(top, {id: 'header'})}
        {this.container(centerParts, {id: 'center'})}
        {this.container(bottom, {id: 'footer'})}
        {this.dialogContainer(dialog)}
      </div>
    )
  }
}

class L131 extends L111 {
  name = () => 'L131'
  componentDidMount() {
    if (canUseDOM) {
      document.documentElement.style.overflow = 'hidden'
    }
  }
  render() {
    let parts = this.alignParts()
    return (
      <div id="flexWrapper" className={this.cssClassName()}>
        {this.container(parts.top, {id: 'header'})}
        <div id="center">
          {this.container(parts.left, {id: 'centerLeft'})}
          {this.container(parts.center, {id: 'centerMiddle'})}
          {this.container(parts.right, {id: 'centerRight'})}
        </div>
        {this.container(parts.bottom, {id: 'footer'})}
        {this.dialogContainer(parts.dialog)}
      </div>
    )
  }
}

const RESPOND_WIDTH = 600
const Responsive = ({parts}) => {
  let [responded, setResponded] = useState(false)
  let onResize = useCallback(
    ({width}) => {
      let needReponse = width < RESPOND_WIDTH
      if (responded !== needReponse) {
        setResponded(needReponse)
      }
    },
    [responded]
  )
  let ref = useResizeObserver({onResize})
  return <div ref={ref}>{responded ? <L111 parts={parts} /> : <L131 parts={parts} />}</div>
}

const TogglePaneButton = ({onClick}) => {
  let [hovered, setHovered] = useState(false)
  let s = {
    cursor: 'pointer',
    backgroundColor: hovered ? '#DDD' : '#EEE',
    position: 'absolute',
    textAlign: 'center',
    fontSize: '0.7em',
  }
  if (hovered) {
    s = {...s, width: '20px', marginLeft: '-10px'}
  }

  return (
    <div
      style={s}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {!hovered ? <>&#160;</> : <>&#10006;</>}
    </div>
  )
}

class L131Split extends L131 {
  /* 131 Layout with splitters
  see: https://leefsmp.github.io/Re-Flex/index.html
  */
  static uiDefaults = {
    left: 0.2,
    right: 0.2,
    __version__: 3,
    __name__: 'L131Split', // shared with descendants
  }

  static storeMap = root => ({
    classname: root.meta.request.classname,
    tabname: root.meta.request.tabname,
    classCfg: selectRequestClassCfg(root),
  })

  static actionMap = {persistSplitter, go: Actions.go}

  constructor(props) {
    super(props)
    this.splitters = {...this.props.ui}
  }
  name = () => 'L131Split'

  shouldComponentUpdate(nextProps, nextState) {
    // no update if only splitter props received
    if (this.needsSplitterUpdate) {
      this.needsSplitterUpdate = false
      return true
    }
    return this.props.parts !== nextProps.parts
  }

  containerElement(parts, cprops) {
    // build contCenter, contleft, contRight
    cprops = {...cprops, className: `cont${ucFirst(cprops.name || cprops.id || '')}`}
    if (has(cprops, 'id')) {
      return <div {...cprops}>{parts}</div>
    } else {
      return (
        <ReflexElement key={cprops.name} {...cprops}>
          <div className="flexSplitterContainer">{parts}</div>
        </ReflexElement>
      )
    }
  }

  onResizePane = event => {
    let {persistSplitter} = this.props
    let {name, flex} = event.component.props

    // fast internal update
    this.splitters[name] = flex

    // persist throttled
    //~ log('send %o %o', name, flex)
    persistSplitter('L131Split', name, flex)
  }

  togglePane = name => {
    let {persistSplitter, ui} = this.props
    let nextValue = 0

    if (!ui[name] || ui[name] < 0.05) {
      nextValue = 0.18
    }
    this.needsSplitterUpdate = true // this is so ugly, but we want to rerender if external left/right props are updated
    this.splitters[name] = nextValue
    persistSplitter('L131Split', name, nextValue) // this will triger a rerender
  }

  centerContainer(parts, cprops) {
    if (!size(parts)) {
      return null
    }
    cprops = {...cprops, className: `cont${ucFirst(cprops.name || cprops.id || '')}`}

    let toptabs = null
    if (parts[0]?.key.startsWith('TreeTopTabs')) {
      toptabs = parts[0]
      parts = parts.slice(1)
    }
    let bottomtabs = null
    if (parts.slice(-1)[0]?.key.startsWith('MajorTabGroup')) {
      bottomtabs = parts.slice(-1)[0]
      parts = parts.slice(0, -1)
    }

    return (
      <ReflexElement {...cprops}>
        <div className="flexSplitterContainer">
          <div style={{display: 'flex', height: '100%', width: '100%'}}>
            <PortalTarget id="centerLeftPortal" />
            <div className="centerFlexWrapper">
              {toptabs}
              <div className="centerFlexContainer">{parts}</div>
              <PortalTarget id="centerBottomPortal" />
              {bottomtabs}
            </div>
            <div>
              <PortalTarget id="centerRightPortal" />
            </div>
          </div>
        </div>
      </ReflexElement>
    )
  }

  render() {
    let parts = this.alignParts()
    let {left, right} = this.splitters
    let center = 1
    if (parts.left) center -= left
    if (parts.right) center -= right
    return (
      <div id="flexWrapper" className={this.cssClassName()}>
        {this.dialogContainer(parts.dialog, {name: 'dialog'})}
        {this.container(parts.top, {name: 'header'})}
        <PortalTarget id="topPortal" />
        <ReflexContainer orientation="vertical">
          {/* PortalTarget needs to be wrapped in Fragment */}
          <>
            <PortalTarget id="leftPortal" />
          </>
          {this.container(parts.left, {
            name: 'left',
            flex: left,
            onResize: this.onResizePane,
          })}
          {parts.left && (
            <ReflexSplitter propagate={true} className="layoutSplitter">
              <TogglePaneButton onClick={() => this.togglePane('left')} />
            </ReflexSplitter>
          )}
          {this.centerContainer(parts.center, {
            name: 'center',
            flex: center,
            minSize: 60,
          })}
          {parts.center && parts.right && (
            <ReflexSplitter propagate={true} className="layoutSplitter" />
          )}
          {this.container(parts.right, {
            name: 'right',
            flex: right,
            onResize: this.onResizePane,
          })}
        </ReflexContainer>
        {this.container(parts.bottom, {name: 'footer'})}
      </div>
    )
  }
}

// L131Split is not available in ssr
L131Split = canUseDOM
  ? connect(
      'L131Split',
      L131Split
    )
  : L131

export {Blank, L1, L1Col, L131, Responsive, L131Split}
